import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import LegalLinks from '../../components/LegalLinks';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, ArrowLeft } from 'lucide-react';
import adminApi from '../../lib/adminApi';
import { setAdminToken, setAdminUser } from '../../lib/adminAuth';
import { getNextPathAfterLogin, clearSessionExpiryFlag } from '../../lib/sessionRedirect';
import { storeSessionTokens } from '../../lib/refreshToken';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('session_expired') === 'true') {
      toast.info('Your session has expired. Please log in again.');
      // Preserve `next` if present so we still restore the target page.
      const next = urlParams.get('next');
      const nextQS = next ? `?next=${encodeURIComponent(next)}` : '';
      window.history.replaceState({}, '', `/admin/login${nextQS}`);
    }
    return () => window.removeEventListener('resize', h);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await adminApi.post('/api/admin/auth/login', formData);
      setAdminToken(response.data.token);
      setAdminUser(response.data.admin);
      storeSessionTokens('admin', response.data);
      clearSessionExpiryFlag();
      toast.success('Welcome back, Admin!');
      navigate(getNextPathAfterLogin('/admin/dashboard'), { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#111111]" data-testid="admin-login-page">
      <div className={`w-full ${isMobile ? 'px-5' : 'max-w-[420px]'}`}>
        {/* Brand */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src="/hed-icon.png" alt="HED" className="w-8 h-8 rounded-lg" />
            <div className="w-px h-6 bg-white/20" />
            <img src="/girgit-logo.png" alt="Girgit" className="w-8 h-8 rounded-lg" />
            <span className="text-xl font-bold text-white">Girgit</span>
          </div>
          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-[#FF6B8A] text-white">Management Portal</span>
        </div>

        {/* Login Card */}
        <div className="bg-white/[0.08] backdrop-blur-xl border border-white/10 rounded-2xl p-8" data-testid="admin-login-card">
          <h2 className="text-xl font-bold text-white mb-1">Welcome Back</h2>
          <p className="text-sm text-neutral-400 mb-8">Sign in to your admin account</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-neutral-400 mb-2">Work Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <input type="email" required value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-sm text-white placeholder-neutral-500 focus:outline-none focus:border-[#FF6B8A]/50 focus:ring-1 focus:ring-[#FF6B8A]/30 transition-colors"
                  placeholder="admin@halfeatendonut.in"
                  data-testid="admin-email-input" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-400 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <input type="password" required value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-sm text-white placeholder-neutral-500 focus:outline-none focus:border-[#FF6B8A]/50 focus:ring-1 focus:ring-[#FF6B8A]/30 transition-colors"
                  placeholder="Enter your password"
                  data-testid="admin-password-input" />
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full py-3 bg-[#FF6B8A] hover:bg-[#E8366D] text-white font-semibold text-sm rounded-xl disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
              data-testid="admin-login-submit">
              {loading ? <><Loader2 className="animate-spin w-4 h-4" />Signing in...</> : 'Sign In'}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center space-y-3">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-white transition-colors" data-testid="admin-back-home">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
          <LegalLinks tone="dark" />
          <p className="text-xs text-neutral-600">&copy; {new Date().getFullYear()} Half Eaten Donut</p>
        </div>
      </div>
    </div>
  );
}
